import { useMemo } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { convertUnit } from '@cast/utils';

import { useInventoryWorkloadsTimeRangeSelection } from '../../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

import { useWorkloadGpuUtilizationSummary } from './useWorkloadGpuUtilizationSummary';

dayjs.extend(utc);

export type GpuMemoryWasteChartPoint = {
  timestamp: string;
  chartTimestampUnix: number;
  available: number;
  used: number;
};

export type GpuMemoryWasteChartTotals = {
  available: number;
  used: number;
  totalWasted: number;
};

export const useGpuMemoryWasteChartData = () => {
  const { timeZone } = useInventoryWorkloadsTimeRangeSelection();
  const {
    intervals,
    totals: apiTotals,
    isLoading,
    error,
    refetch,
  } = useWorkloadGpuUtilizationSummary();

  const data: GpuMemoryWasteChartPoint[] = useMemo(
    () =>
      intervals
        .filter((item) => item.memory)
        .map((item) => ({
          timestamp: item.timestamp,
          chartTimestampUnix: dayjs.utc(item.timestamp).valueOf(),
          available: convertUnit(item.memory!.availableBytes, 'Bytes', 'GiB'),
          used: convertUnit(item.memory!.usedBytes, 'Bytes', 'GiB'),
        })),
    [intervals]
  );

  const totals: GpuMemoryWasteChartTotals = useMemo(
    () => ({
      available: convertUnit(apiTotals.totalGpuMemoryBytes, 'Bytes', 'GiB'),
      used: convertUnit(apiTotals.totalUsedGpuMemoryBytes, 'Bytes', 'GiB'),
      totalWasted: convertUnit(
        apiTotals.totalWastedGpuMemoryBytes,
        'Bytes',
        'GiB'
      ),
    }),
    [apiTotals]
  );

  return {
    data,
    totals,
    isLoading,
    error,
    refetch,
    timeZone,
  };
};
